import { getStoredLegacyToken } from './legacyAuthService';

export interface NotificationItem {
  id: string;
  title: string;
  message: string;
  status: string;
  createdAt: string | null;
  sentAt: string | null;
}

function buildHeaders(includeAuth = false): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (includeAuth) {
    const rawToken =
      getStoredLegacyToken()
      || localStorage.getItem('legacy_token')
      || sessionStorage.getItem('legacy_token')
      || '';
    if (rawToken) {
      headers.Authorization = /^Bearer\s+/i.test(rawToken) ? rawToken : `Bearer ${rawToken}`;
    }
  }

  return headers;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const rawText = await response.text();
  let payload: unknown = null;
  try {
    payload = rawText ? JSON.parse(rawText) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const structuredError =
      (payload
        && typeof payload === 'object'
        && 'error' in payload
        && typeof (payload as { error?: unknown }).error === 'string')
        ? String((payload as { error: string }).error)
        : '';

    const message = structuredError || rawText || `API error (${response.status})`;
    throw new Error(message);
  }

  return (payload as T) || ({} as T);
}

export async function listNotifications(params?: { limit?: number; offset?: number }): Promise<{
  success: boolean;
  notifications: NotificationItem[];
}> {
  const limit = params?.limit ?? 50;
  const offset = params?.offset ?? 0;
  const query = `?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`;

  const response = await fetch(`/api/notifications/list${query}`, {
    method: 'GET',
    headers: buildHeaders(true),
  });

  return parseResponse<{ success: boolean; notifications: NotificationItem[] }>(response);
}

export async function createNotification(payload: {
  title: string;
  message: string;
  send?: boolean;
  topic?: string;
}): Promise<{
  success: boolean;
  notificationId: string;
  status: string;
}> {
  const response = await fetch('/api/notifications/create', {
    method: 'POST',
    headers: buildHeaders(true),
    body: JSON.stringify(payload),
  });

  return parseResponse<{ success: boolean; notificationId: string; status: string }>(response);
}

